/**
 * Non-US shipping restriction.
 *
 * Show the notice and hold back add-to-cart until acknowledged when Shopify's
 * geolocation puts the shopper outside the US. The Cartbox keeps its one button
 * in place and disables it; the CounterCool Buy Box hides its Offers instead.
 * Fail-safe is US behavior.
 */
(function () {
  // Tell the inline guard in layout/theme.liquid that this script is running,
  // so it leaves the Offers hidden instead of falling open to US. Set it first,
  // before any early return below, because the guard only asks whether the
  // script ran at all.
  window.auraCountryGuard = true;

  // The class the inline guard puts on the root element. base.css hides
  // the Offers while it is there, which is what keeps them off the first paint.
  const AWAITING_COUNTRY_CLASS = 'aura-awaiting-country';

  // Shopify's native geolocation route, resolved against the shop root for locale prefixes.
  let ROOT = '/';

  if (window.Shopify && Shopify.routes && Shopify.routes.root) {
    ROOT = Shopify.routes.root;
  }

  const GEO_ENDPOINT = ROOT + 'browsing_context_suggestions.json?country[enabled]=true';
  const COUNTRY_CACHE_KEY = 'aura_visitor_country';
  const FETCH_TIMEOUT_MS = 3000;
  const COUNTRY_CACHE_MS = 24 * 60 * 60 * 1000;

  // Is the shopper confirmed outside the US?
  let isOutsideUS = false;

  // Has the lookup answered yet, from the cached country or the network?
  // Until it has, the Buy Box hides its Offers, so a shopper outside the US
  // never sees a way to buy before the notice.
  let isCountryKnown = false;

  // Single source of the "outside the US" rule.
  function isNonUS(countryCode) {
    if (!countryCode) {
      return false;
    }

    return countryCode !== 'US';
  }

  // Read the cached country, or null when it is missing, unreadable or
  // older than COUNTRY_CACHE_MS. localStorage, not sessionStorage, so a repeat
  // visit knows the country before the lookup runs and the Offers never flash.
  function getCachedCountry() {
    try {
      const stored = localStorage.getItem(COUNTRY_CACHE_KEY);

      if (!stored) {
        return null;
      }

      const cached = JSON.parse(stored);
      const hasBothFields = cached && cached.country && cached.savedAt;

      // Do not proceed without both fields. An older or hand-edited value can
      // miss them, and a country with no date cannot be expired.
      if (!hasBothFields) {
        return null;
      }

      // A device clock set ahead makes this negative. Such a value never passes
      // the limit below, so count it as expired too; otherwise the country
      // stays in place until the clock catches up, which can be days.
      const age = Date.now() - cached.savedAt;
      const isExpired = age < 0 || age > COUNTRY_CACHE_MS;

      // Do not proceed if the cached country is older than COUNTRY_CACHE_MS or
      // dated ahead of the clock. A shopper can travel or switch a VPN on, so a
      // country we cannot date must not rule the page.
      if (isExpired) {
        return null;
      }

      return cached.country;
    } catch (e) {
      return null;
    }
  }

  // Cache the country with the date it was read. Ignore failures (private
  // mode / disabled).
  function setCachedCountry(countryCode) {
    try {
      const cached = { country: countryCode, savedAt: Date.now() };

      localStorage.setItem(COUNTRY_CACHE_KEY, JSON.stringify(cached));
    } catch (e) {
      return;
    }
  }

  // Get the notice, checkbox, Buy Box Offers, and every add-to-cart button the
  // notice guards.
  function getElements() {
    // Other /cart/add forms can exist (e.g. samita wholesale), so never grab the first.
    // The CounterCool Buy Box shows one button per Offer, so take them all.
    const buttons = document.querySelectorAll(
      '.aura-cartbox__add-to-cart-btn, .aura-buy-box__add-to-cart-btn',
    );

    return {
      notice: document.querySelector('[data-us-shipping-restriction]'),
      checkbox: document.querySelector('[data-us-shipping-acknowledgement]'),
      offers: document.querySelector('[aura-buy-box-offers]'),
      buttons: Array.from(buttons),
    };
  }

  // Apply the current state to the DOM. Safe to call repeatedly.
  function render() {
    const els = getElements();

    // Do not proceed without a notice or a button. Nothing to show, nothing
    // to guard.
    if (!els.notice || els.buttons.length === 0) {
      return;
    }

    // Show the notice only outside the US.
    els.notice.hidden = !isOutsideUS;

    // Outside the US, block add-to-cart until the shopper acknowledges.
    // A missing checkbox counts as acknowledged, so the button stays on.
    const acknowledged = els.checkbox && els.checkbox.checked;
    const blockAddToCart = isOutsideUS && !acknowledged;

    // The CounterCool Buy Box holds one button per Offer, so it hides the whole
    // Offers panel and the page then shows no way to buy. An unknown country
    // hides them too, so the first visit of a device waits for the lookup
    // instead of showing Offers it may have to take away again.
    //
    // Stop there and leave the buttons alone: aura-countercool-buy-box.js keeps
    // its own in-flight lock in aria-disabled, and clearing that here would let
    // one Offer be added twice. A page without the panel, such as a Cartbox
    // product, falls through and only has its button to work with.
    const hideOffers = !isCountryKnown || blockAddToCart;

    if (els.offers) {
      els.offers.hidden = hideOffers;
      return;
    }

    // aria-disabled (not native disabled) keeps the button focusable and reuses
    // the submit guards in product-form.js and the Buy Box, which both bail on it.
    els.buttons.forEach(function (button) {
      if (blockAddToCart) {
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      button.removeAttribute('aria-disabled');
    });
  }

  // Bind listeners once, up front.
  function bindEvents() {
    const els = getElements();

    if (els.checkbox) {
      els.checkbox.addEventListener('change', render);
    }
  }

  // Resolve the visitor's ISO country code, or null if it cannot be determined.
  function detectCountry() {
    const cachedCountry = getCachedCountry();
    if (cachedCountry) {
      return Promise.resolve(cachedCountry);
    }

    // Abort the lookup if it runs past the timeout.
    const controller = new AbortController();
    const timer = setTimeout(function () {
      controller.abort();
    }, FETCH_TIMEOUT_MS);

    // Do not reuse a cached HTTP response, so detection reflects the current IP.
    const opts = { cache: 'no-store', signal: controller.signal };

    return fetch(GEO_ENDPOINT, opts)
      .then(function (res) {
        if (res.ok) {
          return res.json();
        }

        return null;
      })
      .then(function (data) {
        clearTimeout(timer);

        // detected_values.country is the IP-detected physical country, not the selected market.
        let country = null;
        if (data && data.detected_values) {
          country = data.detected_values.country;
        }

        let countryCode = null;
        if (country && country.handle) {
          countryCode = String(country.handle).toUpperCase();
        }

        if (countryCode) {
          setCachedCountry(countryCode);
        }

        return countryCode;
      })
      .catch(function () {
        clearTimeout(timer);

        // Any failure means unknown, treated as US.
        return null;
      });
  }

  // Set the module state from a country code and re-render. A null code means
  // the lookup failed, which still counts as answered and behaves as US.
  function applyCountry(countryCode) {
    isOutsideUS = isNonUS(countryCode);
    isCountryKnown = true;

    // The country is settled, so hand the Offers back to render(), which
    // decides whether they show. Leaving the class on would hide them for good.
    document.documentElement.classList.remove(AWAITING_COUNTRY_CLASS);

    render();
  }

  function init() {
    // Do not proceed if there is no notice on this page.
    if (!document.querySelector('[data-us-shipping-restriction]')) {
      return;
    }

    bindEvents();

    // Paint the cached country right away, so a repeat visit is correct before
    // the lookup runs. With nothing cached, this first render hides the Offers
    // and the lookup below decides what the shopper sees.
    const cachedCountry = getCachedCountry();

    if (cachedCountry) {
      applyCountry(cachedCountry);
    } else {
      render();
    }

    detectCountry().then(applyCountry);
  }

  // The script is deferred, so the DOM is ready by the time it runs.
  init();

  // Back/forward restore: the browser keeps the whole page in memory, including
  // the JavaScript heap, so the shopper comes back to the checkbox exactly as
  // they left it. See https://developer.mozilla.org/en-US/docs/Glossary/bfcache
  //
  // The acknowledgment is meant to be given once per page load, so clear the
  // checkbox here. Then re-apply the cached country without re-fetching, which
  // avoids flicker.
  window.addEventListener('pageshow', function () {
    const els = getElements();

    if (els.checkbox) {
      els.checkbox.checked = false;
    }

    const countryCode = getCachedCountry();

    // Nothing cached, so there is no country to re-apply. Render anyway, so the
    // cleared checkbox reaches the page.
    if (countryCode === null) {
      render();
      return;
    }

    applyCountry(countryCode);
  });
})();
